import React, { useEffect, useMemo, useState, useCallback } from "react";
import { doc, onSnapshot, collection } from "firebase/firestore";
import RequestReplyTab from "./RequestReplyTab";
import { FlightRequest, FlightDetail } from "./Types";
import SearchBar from "./SearchBar";
import SortDropdown from "./SortDropdown";
import {
  getFlightRequests,
  getOpenRequestCount,
  lockUnlockResponse,
} from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { auth, firestore } from "config/firebaseConfig";
import DefaultAirLogo from "../../assets/images/logo.svg";
import { useToastStore } from "store/toastStore";
import { useCreditStore } from "store/creditStore";
import Pagination from "../../components/Pagination";

interface LockStatus {
  requestDetailId: number;
  isLocked: boolean;
}

export const OpenRequest: React.FC = () => {
  const [selectedFlight, setSelectedFlight] = useState<FlightRequest | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("DepartureTime");
  const { showLoader, hideLoader } = useGlobalLoader();
  const [flightRequests, setFlightRequests] = useState<FlightRequest[]>([]);
  const [selectedFlightDetail, setSelectedFlightDetail] =
    useState<FlightDetail | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [flightResponse, setFlightResponse] = useState<any>();
  const [lockStatuses, setLockStatuses] = useState<LockStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const status = "open";
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [openRequestCount, setOpenRequestCount] = useState<number>(0);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Debounce function
  const debounce = <F extends (...args: any[]) => any>(
    func: F,
    wait: number
  ) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<F>): Promise<ReturnType<F>> => {
      return new Promise((resolve) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => resolve(func(...args)), wait);
      });
    };
  };

  // Fetch open request count
  const fetchOpenRequestCount = useCallback(async () => {
    try {
      const res: any = await getOpenRequestCount();
      // API returns { Count: <number> } (serialized as Count/count).
      const count = res?.Count ?? res?.count ?? res ?? 0;
      setOpenRequestCount(Number(count) || 0);
    } catch (error) {
      console.error("Error fetching open request count:", error);
      setError("Failed to fetch open request count");
    }
  }, []);

  // Map display names to API sortBy values
  const mapSortByToApiValue = (displayName: string): string => {
    switch (displayName) {
      case "Departure Time":
        return "DepartureTime";
      case "Flight Number":
        return "FlightNumber";
      case "Route":
        return "Route";
      default:
        return "DepartureTime";
    }
  };

  // Fetch flight requests
  const fetchFlightRequests = useCallback(
    debounce(async () => {
      showLoader();
      try {
        const response = await getFlightRequests({
          pageNumber: currentPage,
          pageSize,
          status,
          sortBy: sortBy,
        });
        setError(null);
        setFlightResponse(response);
        const requests = response.items.map((r: any) => ({
          ...r,
          departureDate: `${new Date(r.departure).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })} - ${new Date(r.departure).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}`,
          departureTime: r.departure
            ? r.departure.split("T")[1].slice(0, 5)
            : "",

          arrivalTime: r.arrival
            ? r.arrival.split("T")[1].slice(0, 5)
            : "",
          duration: r.elptim
          ? `${parseInt(r.elptim.slice(0, -2))}h ${parseInt(
              r.elptim.slice(-2)
            )}m`
          : "",
        }));
        setFlightRequests(requests);
      } catch (error) {
        console.error("Error:", error);
        setFlightRequests([]);
      } finally {
        setIsRefreshing(false);
        setTimeout(() => hideLoader(), 500);
      }
    }, 500),
    [currentPage, reloadTrigger, sortBy, pageSize]
  );

  // Manual refresh — re-fetch the list and the open-request count.
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchOpenRequestCount();
    setReloadTrigger((prev) => prev + 1);
  };

  // Set up real-time listener for lock statuses
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setError("User not authenticated");
      setLockStatuses(
        flightRequests.map((r) => ({
          requestDetailId: r.requestDetailId,
          isLocked: false,
        }))
      );
      return;
    }

    const uid = user.uid;
    const requestsCollectionRef = collection(
      firestore,
      "userRequests",
      uid,
      "requests"
    );

    // Set up the real-time listener
    const unsubscribe = onSnapshot(
      requestsCollectionRef,
      (snapshot) => {
        const updatedStatuses: LockStatus[] = flightRequests.map((r) => {
          const doc = snapshot.docs.find(
            (d) => d.id === String(r.requestDetailId)
          );
          if (doc && doc.exists()) {
            const data = doc.data();
            const isLocked = data.status === "In Progress";
            if (isLocked) {
              setExpiresAt(data.expiresAt);
            }
            return { requestDetailId: r.requestDetailId, isLocked };
          }
          return { requestDetailId: r.requestDetailId, isLocked: false };
        });
        setLockStatuses(updatedStatuses);
      },
      (error) => {
        console.error("Error in Firestore listener:", error);
        setError("Failed to fetch lock statuses");
      }
    );

    // Clean up the listener on component unmount
    return () => unsubscribe();
  }, [flightRequests]);

  // Auto-close modal when expiresAt is reached
  useEffect(() => {
    if (!isModalOpen || !expiresAt) return;

    const checkExpiration = () => {
      const currentTime = new Date().getTime();
      const expirationTime = new Date(expiresAt).getTime();
      if (currentTime >= expirationTime) {
        handleCloseModal();
      }
    };

    const intervalId = setInterval(checkExpiration, 1000); // Check every second
    checkExpiration(); // Immediate check in case already expired

    return () => clearInterval(intervalId); // Clean up interval
  }, [isModalOpen, expiresAt]);

  // Reset currentPage to 1 when sortBy changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  // Fetch flight requests and open request count
  useEffect(() => {
    fetchOpenRequestCount();
    fetchFlightRequests();
  }, [fetchFlightRequests]);

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightResponse?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  // Change rows-per-page and jump back to the first page.
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleCardClick = async (flight: FlightRequest) => {
    const lockStatus = lockStatuses.find(
      (s) => s.requestDetailId === flight.requestDetailId
    );
    if (lockStatus?.isLocked) return;

    // Open the modal immediately for instant feedback; acquire the lock in the
    // background. If the lock fails (e.g. someone else is already answering),
    // back out and notify.
    const flightDetail: FlightDetail = {
      logoImg: flight.airlineLogo,
      airlineId: flight.airlineId,
      airlineName: flight.airlineName,
      flightNumber: flight.flightNumber,
      departureAirport: flight.sourceCode,
      arrivalAirport: flight.destinationCode,
      departureTime: flight.departureTime,
      arrivalTime: flight.arrivalTime,
      flightDuration: flight.duration,
      requestDetailId: flight.requestDetailId,
      flightDate: new Date(flight.flightDate)
        .toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          weekday: "short",
          timeZone: "UTC",
        })
        .toUpperCase()
        .replace(/^(\w+),\s(\w+)\s(\d+)$/, "$2 $3 - $1"),
    };
    setSelectedFlight(flight);
    setSelectedFlightDetail(flightDetail);
    setIsModalOpen(true);

    try {
      await lockUnlockResponse(Number(flight.requestDetailId), false);
    } catch (error: any) {
      // Roll back the optimistic open.
      setIsModalOpen(false);
      setSelectedFlight(null);
      setSelectedFlightDetail(null);
      const message =
        error?.response?.status === 423
          ? "This request is currently being answered by someone else."
          : "Failed to open this request. Please try again.";
      useToastStore.getState().showToast(message, "error", 3000);
      console.error("Error locking flight request:", error);
    }
  };

  const handleCloseModal = () => {
    const detailId = selectedFlightDetail?.requestDetailId;
    // Close immediately; release the lock in the background.
    setIsModalOpen(false);
    setSelectedFlight(null);
    setSelectedFlightDetail(null);
    if (detailId) {
      lockUnlockResponse(Number(detailId), true).catch((error) => {
        console.error("Error unlocking flight request:", error);
      });
    }
  };

  // Called after a successful answer: drop the request from the open list
  // immediately, refresh the earned-token balance, then close (unlock in bg).
  const handleSubmit = () => {
    const answeredId = selectedFlightDetail?.requestDetailId;
    if (answeredId != null) {
      setFlightRequests((prev) =>
        prev.filter((r) => r.requestDetailId !== answeredId)
      );
    }
    // Answering earns tokens — pull the new balance for the sidebar.
    useCreditStore.getState().refresh();
    fetchOpenRequestCount();
    handleCloseModal();
  };

  const filteredFlights = useMemo(() => {
    if (!Array.isArray(flightRequests)) return [];
    const result = flightRequests
      .filter((flight) => flight.status === "Open")
      .filter(
        (flight) =>
          flight.airlineName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          flight.flightNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    // Client-side sorting removed - API handles sorting via sortBy parameter
    return result;
  }, [flightRequests, searchQuery]);

  return (
    <section className="mt-0">
      {error && <div className="text-red-500 text-center mb-4">{error}</div>}
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4 mb-2">
        <p className="text-[#1F2937] text-base font-semibold flex items-center gap-2">
          Open Requests
          <span className="flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded-full bg-[#FBBF24] text-white">
            {openRequestCount}
          </span>
        </p>
        <div className="flex items-center gap-3 relative">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center justify-center w-10 h-10 bg-white border border-[#EDEEF1] rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
            title="Refresh"
            aria-label="Refresh"
          >
            <i
              className={`st-reload text-[#1F2937] ${isRefreshing ? "animate-spin" : ""}`}
            ></i>
          </button>
          <SortDropdown
            currentSortBy={sortBy}
            onSortChange={(displayName) => {
              const apiValue = mapSortByToApiValue(displayName);
              setSortBy(apiValue);
            }}
          />
        </div>
      </div>
      {filteredFlights.length === 0 ? (
        <div className="flex justify-center items-center h-40 w-full text-[#6B7280]">
          <p>No flight requests found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFlights.map((flight: any, index) => {
            const lockStatus = lockStatuses.find(
              (s) => s.requestDetailId === flight.requestDetailId
            );
            const isLocked = lockStatus?.isLocked ?? false;
            return (
              <div
                key={index}
                className={`relative group grid grid-cols-12 gap-4 items-center px-3 sm:px-5 py-3 sm:py-4 bg-white border rounded-xl transition-all duration-200 ease-out ${
                  !isLocked
                    ? "cursor-pointer border-[#EDEEF1] hover:shadow-md hover:-translate-y-0.5"
                    : "cursor-not-allowed border-[#EDEEF1]"
                }`}
                onClick={() => handleCardClick(flight)}
                title={
                  isLocked
                    ? "This request is currently being answered by someone else."
                    : undefined
                }
              >
                {isLocked && (
                  <>
                    {/* Dim the card content while keeping the badge/tooltip crisp. */}
                    <div className="absolute inset-0 rounded-xl bg-white/55 z-[1] pointer-events-none" />
                    {/* Persistent "In use" badge + richer tooltip on hover. */}
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 rounded-full bg-[#FDECEC] px-2.5 py-1 pointer-events-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-3 h-3 text-[#DC4043]"
                      >
                        <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm3 8H9V6a3 3 0 0 1 6 0v3Z" />
                      </svg>
                      <span className="text-[11px] font-semibold text-[#DC4043] leading-none">
                        In use
                      </span>
                      <span className="pointer-events-none absolute top-full right-0 mt-1.5 hidden group-hover:block whitespace-nowrap rounded-md bg-[#1F2937] px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg">
                        This request is currently being answered by someone else.
                      </span>
                    </div>
                  </>
                )}
                {/* Airline */}
                <div className="flex items-center lg:col-span-2 md:col-span-4 col-span-12 gap-3">
                  <img
                    className="w-10 h-10 rounded-lg object-contain"
                    src={flight.airlineLogo || DefaultAirLogo}
                    alt={flight.airlineName?.toLowerCase() || "Airline"}
                    onError={(e) =>
                      (e.currentTarget.src = "/assets/logos/air-logo.png")
                    }
                  />
                  <div>
                    <p className="text-[#1F2937] text-sm font-semibold">{flight.airlineName}</p>
                    <p className="text-[#6B7280] text-xs">{flight.flightNumber}</p>
                  </div>
                </div>

                {/* Date */}
                <div className="flex items-center lg:col-start-4 lg:col-span-4 md:col-span-8 col-span-12 gap-6">
                  <div>
                    <p className="text-[#6B7280] text-[11px]">Date</p>
                    <p className="text-[#1F2937] text-sm font-medium">
                      {(() => {
                        const date = new Date(flight?.flightDate);
                        const day = String(date.getUTCDate()).padStart(2, "0");
                        const month = date.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
                        const year = date.getUTCFullYear();
                        return `${day} ${month}, ${year}`;
                      })()}
                    </p>
                  </div>
                </div>

                {/* Route */}
                <div className="flex items-center lg:col-span-5 md:col-span-12 col-span-12 gap-3">
                  <div className="flex flex-col items-start gap-0.5 min-w-[50px]">
                    <p className="text-[#1F2937] text-sm font-semibold">{flight.sourceCode}</p>
                    <p className="text-[#6B7280] text-[11px]">{flight.departureTime}</p>
                  </div>

                  <div className="flex flex-col items-center w-full">
                    <div className="flex items-center w-full">
                      <div className="w-2 h-2 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                      <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1"></div>
                      <i className="st-flight-slim text-[#737C8C] text-sm mx-1"></i>
                      <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1"></div>
                      <div className="w-2 h-2 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                    </div>
                    <p className="text-[#6B7280] text-[10px] mt-0.5">{flight.duration}</p>
                  </div>

                  <div className="flex flex-col items-end gap-0.5 min-w-[50px]">
                    <p className="text-[#1F2937] text-sm font-semibold">{flight.destinationCode}</p>
                    <p className="text-[#6B7280] text-[11px]">{flight.arrivalTime}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selectedFlightDetail && (
        <RequestReplyTab
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          logoImg={selectedFlightDetail.logoImg}
          airlineId={Number(selectedFlightDetail.airlineId)}
          airlineName={selectedFlightDetail.airlineName}
          flightNumber={selectedFlightDetail.flightNumber}
          flightDate={selectedFlightDetail.flightDate}
          departureAirport={selectedFlightDetail.departureAirport}
          arrivalAirport={selectedFlightDetail.arrivalAirport}
          departureTime={selectedFlightDetail.departureTime}
          arrivalTime={selectedFlightDetail.arrivalTime}
          flightDuration={selectedFlightDetail.flightDuration}
          requestDetailId={selectedFlightDetail.requestDetailId}
        />
      )}
      <section>
        <Pagination
          currentPage={currentPage}
          totalCount={flightResponse?.totalCount ?? 0}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showTotal
        />
      </section>
    </section>
  );
};

export default OpenRequest;
